import { useEffect, useRef, useState } from 'react';

export const MinActivityLabelDwellMs = 1500;

/** Keeps a vanished activity label on screen until it has been visible for at
 *  least `minDwellMs`, but only while `hold` is true (a turn is in flight).
 *
 *  The agent clears a tool-call label the instant the tool returns — often a
 *  few hundred ms — which reads as a flash before the "Thinking..." fallback.
 *  A new label always replaces the held one immediately; dropping `hold`
 *  (turn ended) clears at once. */
export function useDwelledLabel(
  label: string | undefined,
  hold: boolean,
  minDwellMs = MinActivityLabelDwellMs
): string | undefined {
  const [held, setHeld] = useState(label);
  const shownAtRef = useRef(0);

  useEffect(() => {
    if (label !== undefined) {
      shownAtRef.current = Date.now();
      setHeld(label);
      return;
    }
    if (!hold) {
      setHeld(undefined);
      return;
    }
    const remaining = minDwellMs - (Date.now() - shownAtRef.current);
    if (remaining <= 0) {
      setHeld(undefined);
      return;
    }
    const timer = setTimeout(() => setHeld(undefined), remaining);
    return () => clearTimeout(timer);
  }, [label, hold, minDwellMs]);

  return label ?? held;
}
